import { type FC } from 'react';

import { Box } from '../Box';
import { type IconSize, Icon } from '../Icon';

type StarSizeVariant = 'sm' | 'md';

const starSize: Record<StarSizeVariant, { size: IconSize; spacing: string }> = {
  sm: {
    size: 'medium',
    spacing: 'var(--spacing-2)',
  },
  md: {
    size: 'large',
    spacing: 'var(--spacing-1)',
  },
};

export type StarRatingProps = {
  /**
   * Rating value from 1 to 5
   */
  rating: number;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Size of the star icons
   */
  size?: StarSizeVariant;
};

const StarRating: FC<StarRatingProps> = ({
  rating,
  className,
  size = 'md',
}) => {
  const stars = [];

  // Helper function to determine star type based on rating rules
  const getStarType = (
    position: number,
    rating: number
  ): 'full' | 'half' | 'empty' => {
    // Round to 2 decimal places to avoid floating-point precision issues
    const decimal = Math.round((rating - Math.floor(rating)) * 100) / 100;
    const wholePart = Math.floor(rating);

    if (position <= wholePart) {
      return 'full';
    }

    if (position === wholePart + 1) {
      // Rules for half stars:
      // 0.0-0.2: no half star (empty)
      // 0.3-0.7: half star
      // 0.8-1.0: full star
      if (decimal >= 0.8) {
        return 'full';
      }
      if (decimal >= 0.3) {
        return 'half';
      }
    }

    return 'empty';
  };

  for (let i = 1; i <= 5; i++) {
    const starType = getStarType(i, rating);

    let variant: 'StarSolid' | 'StarHalf' = 'StarSolid';
    let fill = 'var(--color-neutral-100)';

    if (starType === 'full') {
      variant = 'StarSolid';
      fill = 'var(--color-yellow-500)';
    } else if (starType === 'half') {
      variant = 'StarHalf';
      fill = 'var(--color-yellow-500)';
    } else {
      variant = 'StarSolid';
      fill = 'var(--color-neutral-100)';
    }

    stars.push(
      <Icon key={i} variant={variant} size={starSize[size].size} fill={fill} />
    );
  }

  return (
    <Box
      className={className}
      display="flex"
      alignItems="center"
      gap={starSize[size].spacing}
    >
      {stars}
    </Box>
  );
};

export default StarRating;
